/* eslint-disable no-irregular-whitespace */
import { noop } from '@mail-core/std';
import { Spacing } from '@vkontakte/vkui';
import React, { memo, useCallback, useEffect } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import {
    type UnlimitedDocumentsEntryPoint,
    UNLIMITED_DOCUMENTS_RADAR_NAMES,
} from 'reactApp/modules/unlimitedDocuments/unlimitedDocuments.const';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './UnlimitedDocumentsThanksModal.css';
import { getUnlimitedDocumentsThanksModalCoverImageUrl } from './UnlimitedDocumentsThanksModal.helpers';

export interface UnlimitedDocumentsThanksModalProps {
    onClose?: () => void;
    onClick?: () => void;
    onShow?: () => void;
    onCancel?: () => void;

    offer_source: UnlimitedDocumentsEntryPoint;
}

export const UnlimitedDocumentsThanksModal = memo<UnlimitedDocumentsThanksModalProps>(
    ({ onShow = noop, onClick = noop, onClose = noop, onCancel = noop, offer_source }) => {
        const isDarkTheme = isDarkThemeModeEnabled();
        const coverImage = getUnlimitedDocumentsThanksModalCoverImageUrl(isDarkTheme);

        const sendAnalytics = useCallback((action: string) => {
            sendPaymentGa({
                action,
                name: UNLIMITED_DOCUMENTS_RADAR_NAMES.THANKS_MODAL,
                eventCategory: ECategoryGa.modal,
                offer_source,
            });
        }, []);

        useEffect(() => {
            sendAnalytics('show');
            emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_THANKS_MODAL_SHOW, { offer_source });
            onShow();
        }, [onShow, sendAnalytics, offer_source]);

        const handleOnPrimaryClick = useCallback(() => {
            sendAnalytics('click');
            emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_THANKS_MODAL_CLICK, { offer_source });
            onClick();
        }, [onClick, sendAnalytics, offer_source]);

        const handleOnCancel = useCallback(() => {
            sendAnalytics('close');
            emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_THANKS_MODAL_CLOSE, { offer_source });
            onCancel();
        }, [onCancel, sendAnalytics, offer_source]);

        return (
            <WhatsNewDialog
                imageUrl={coverImage}
                imageClassName={styles.image}
                title="Спасибо за интерес"
                content={
                    <>
                        <Spacing size={12} />
                        <div className={styles.text}>
                            Пока работаем над новыми возможностями в подписке Mail Space — благодаря вам теперь знаем,
                            что это было бы полезно
                        </div>
                        <Spacing size={32} />
                    </>
                }
                primaryButtonText="Понятно"
                onClick={handleOnPrimaryClick}
                onClose={onClose}
                onDismiss={handleOnCancel}
                imageBgColor="var(--vkui--color_background_modal)"
                buttonTheme="vk"
                dialogSize="tiny"
                qaId="unlimited-documents-thanks-modal"
                isRebranding
            />
        );
    }
);

UnlimitedDocumentsThanksModal.displayName = 'UnlimitedDocumentsThanksModal';
